
import { Options, Ref, Vue } from "vue-property-decorator";
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import ModalBox from '@/shared/components/modal-box/index.vue';
import { TrustAPI } from '@/shared/modules/trust-api';
import { EventEmitter } from 'events';
import store from '@/trust-admin-app/store';
import { Inject } from 'vue-property-decorator';
import { AppInsights } from '@/trust-admin-app/appInsights';
import PostcodeSearch from "@/shared/components/postcodeSearch/index.vue";
import { AddExternalExaminerRequest, SuburbStatePostcode, UpdateExternalExaminerRequest } from "@/shared/modules/typescript-trustapi-client/models";
import FileDragAndDrop from "@/shared/components/file-drag-and-drop/index.vue";
import ConfirmDialogue from '@/shared/components/confirm-dialogue/index.vue';

@Options({
  props: {
    selectedEE: Object,
  },
  components: {
    ModalBox,
    LoadingIndicator,
    PostcodeSearch,
    FileDragAndDrop,
    ConfirmDialogue,
  }
})
export default class AddEeModal extends Vue {
  private selectedEE!: any;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private ai = new AppInsights;

  @Ref('fileDropAndDrop') readonly fileDropAndDrop!: any;

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private line1Text = 'Add new External Examiner';
  private suburbStatePostcode: SuburbStatePostcode = {};
  private isLoading = false;
  private fileList: Array<File> = [];
  private uploadedFileList: Array<any> = [];
  private uploading = false;
  private submitting = false;
  private submitPressed = false;

  private lawId = '';
  private firmName = '';
  private email = '';
  private address = '';
  private firstName = '';
  private lastName = '';
  private phoneNumber = null;
  private reasonForInactive = '';
  private inactive = false;
  private publishOnWeb = true; // Default to true

  private invalidLawIdEmail = false;
  private dirtyFields = [false, false, false, false, false, false];

  private membershipStatusTypes: any = [];
  private selectedMembershipType: any = null;
  private inEditMode = false;

  private loadingDataSet = false;
  private searchText = '';
  private isSearching = false;

  created(): void {
    this.preloadData();
    this.dirtyFields = [false, false, false, false, false, false];
  }

  mounted(): void {
    this.line1Text = this.selectedEE ? 'Edit External Examiner' : 'Add new External Examiner';
    this.inEditMode = this.selectedEE ? true : false;
    if (this.selectedEE) {
      this.firmName = this.selectedEE.firmName;
      this.address = this.selectedEE.address;
      this.email = this.selectedEE.email;
      this.firstName = this.selectedEE.firstName || '';
      this.lastName = this.selectedEE.lastName || '';
      this.reasonForInactive = this.selectedEE.reasonForInactive || '';
      this.inactive = this.selectedEE.inactive;
      this.publishOnWeb = this.selectedEE.publishOnWeb;
      this.suburbStatePostcode = {
        suburbName: this.selectedEE.suburb,
        postcode: this.selectedEE.postCode,
        stateName: this.selectedEE.state,
        stateCode: this.selectedEE.state,
      }

      this.phoneNumber = this.selectedEE.phoneDetail?.phoneNumber;
      this.selectedMembershipType = this.selectedEE.eeMembershipType;
      this.uploadedFileList = this.selectedEE.files;
    }
  }

  private async preloadData(): Promise<void> {
    try {
      this.loadingDataSet = true;
      const resp: any = await Promise.all([
        this.apiService.CodeApi?.apiV2CodeGetEeMembershipTypesGet(),
      ]);
      this.membershipStatusTypes = resp[0].data.data;
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to load Membership type: ' + err);
    } finally {
      this.loadingDataSet = false;
    }
  }

  private async searchLawIDforEE(): Promise<void> {
    this.isSearching = true;
    this.invalidLawIdEmail = false;
    this.apiService.LawIdApi?.apiV2LawIdAdminSearchLawIdUsersForExternalExaminerPost({ keyword: this.searchText })
      .then((res: any) => {
        if (res) {
          this.lawId = res.data.lawId || '';
          this.email = res.data.email || '';
          this.firstName = res.data.firstName || '';
          this.lastName = res.data.surname || '';
          this.isSearching = false;
        } else {
          this.eventCtrl.emit('show-snackbar', 'error', 'No LawID found with this email');
          this.isSearching = false;
        }
      })
      .catch(err => {
        if (err.response.status === 400) {
          this.invalidLawIdEmail = true;
          // this.eventCtrl.emit('show-snackbar', 'error', 'Error: ' + err.response.data.message);
        } else {
          this.eventCtrl.emit('show-snackbar', 'error', 'Unable to search LawID/email: ' + err);
        }
        this.isSearching = false;
      })
  }

  private closeModal() {
    this.$emit('closeEEModal');
  }

  get btnText(): string {
    return this.inEditMode ? 'Update' : 'Add';
  }

  private updateSuburb(s: SuburbStatePostcode) {
    this.suburbStatePostcode = s;
  }

  private isFormValid(): boolean {
    if (this.inEditMode) {
      return !!this.firmName && !!this.address && !!this.phoneNumber &&
        !!this.suburbStatePostcode?.postcode && this.selectedMembershipType != null &&
        (this.inactive && this.reasonForInactive.length > 0 || !this.inactive);
    } else {
      return !!this.firmName && !!this.address && !!this.phoneNumber &&
        !!this.suburbStatePostcode?.postcode && this.selectedMembershipType != null &&
        this.uploadedFileList.length > 0 && (this.inactive && this.reasonForInactive.length > 0 || !this.inactive);
    }
  }

  get suburbError(): boolean {
    return this.submitPressed && !this.suburbStatePostcode?.postcode;
  }

  private async addEE(): Promise<void> {
    try {
      this.submitPressed = true;
      if (!this.isFormValid()) {
        throw ('Incomplete form');
      }
      this.submitting = true;

      if (!this.inEditMode) {
        const body: AddExternalExaminerRequest = {
          id: this.inEditMode ? this.selectedEE.id : 0,
          lawId: this.lawId,
          firmName: this.firmName,
          address: this.address,
          suburb: this.suburbStatePostcode.suburbName,
          postCode: this.suburbStatePostcode.postcode,
          state: this.suburbStatePostcode.stateCode,
          phoneDetail: {
            countryCode: '+61',
            phoneNumber: this.phoneNumber
          },
          eeMembershipType: this.selectedMembershipType,
          files: this.uploadedFileList.map(x => { return { id: x.id }; })
        };
        await this.apiService.ExternalExaminerApi?.apiV2ExternalExaminerAdminAddExternalExaminerPost(body);
        this.eventCtrl.emit('show-snackbar', 'success', 'Application has been submitted');
      } else {
        const body: UpdateExternalExaminerRequest = {
          id: this.selectedEE.id,
          // lawId: this.selectedEE.lawId,
          firmName: this.firmName,
          address: this.address,
          suburb: this.suburbStatePostcode.suburbName,
          postCode: this.suburbStatePostcode.postcode,
          state: this.suburbStatePostcode.stateCode,
          reasonForInactive: this.inactive ? this.reasonForInactive : '',
          inactive: this.inactive,
          publishOnWeb: this.publishOnWeb,
          phoneDetail: {
            countryCode: '+61',
            phoneNumber: this.phoneNumber
          },
          eeMembershipType: this.selectedMembershipType,
          files: this.uploadedFileList.map(x => { return { id: x.id }; })
        }
        await this.apiService.ExternalExaminerApi?.apiV2ExternalExaminerAdminUpdateExternalExaminerPost(body);
        this.eventCtrl.emit('show-snackbar', 'success', 'External Examiner has been updated');
        this.$emit('updateEE');
      }
      this.closeModal();
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error: ' + err);
    } finally {
      this.submitting = false;
    }
  }

  private fileListChanged(fileList: Array<File>): void {
    this.fileList = fileList;
    this.uploadAllFiles(this.fileList);
  }

  private deleteFile(id: number, removeOnly: boolean): void {
    const _confirm: any = this.$refs.confirmDialogue;
    _confirm.show({
      title: 'Delete File',
      message: 'Are you sure you want to delete this file?',
      okButton: 'Delete',
      cancelButton: 'Cancel',
    }).then((res: any) => {
      if (res) {
        this.deleteFileFromServer(id, removeOnly);
      }
    })

  }

  private async deleteFileFromServer(id: number, removeOnly: boolean): Promise<void> {
    if (removeOnly) {
      this.uploadedFileList = this.uploadedFileList.filter(x => x.id !== id);
      this.eventCtrl.emit('show-snackbar', 'success', 'File removed.');
      return;
    } else {
      try {
        await this.apiService.FileAtcsApi?.apiV2FileAtcsAdminDeleteFileFileIdDelete(id);
        this.uploadedFileList = this.uploadedFileList.filter(x => x.id !== id);
        this.eventCtrl.emit('show-snackbar', 'success', 'File removed.');
      } catch (err) {
        // this.ai.trackException('Error on delete file: ' + JSON.stringify(err));
        this.eventCtrl.emit('show-snackbar', 'error', 'Error on delete file: ' + err);
      }
    }

  }

  private async uploadAllFiles(fileList: Array<File>): Promise<void> {
    const allRequests: Array<Promise<boolean>> = [];
    this.uploading = true;
    try {
      if (fileList.length > 0) {
        fileList.forEach((x) => allRequests.push(this.uploadFile(x)));
        await Promise.all(allRequests);
        this.eventCtrl.emit('show-snackbar', 'success', 'File(s) uploaded');
        this.fileList = [];
        this.fileDropAndDrop.selectedFiles = [];
      }
    } catch (err: any) {
      // this.ai.trackException('Error on upload file: ' + JSON.stringify(err));
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on upload file: ' + err);
    } finally {
      this.uploading = false;
    }
  }

  private async uploadFile(file: File): Promise<boolean> {
    try {
      const fileStr = await this.fileToString(file);
      const body = {
        fileName: file.name,
        fileExtension: file.name,
        fileDataUrl: fileStr,
        id: ''
      };

      const res: any = await this.apiService.FileAtcsApi?.apiV2FileAtcsAdminSaveExternalExaminerFilePost(body);
      body.id = res.data.data;
      body.fileDataUrl = '';
      this.uploadedFileList.push(body);

      return Promise.resolve(true);
    } catch (err) {
      console.log(err);
      // this.ai.trackException('Error on upload file: ' + JSON.stringify(err));
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on upload file: ' + err);
      return Promise.resolve(false);
    }
  }

  private fileToString(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        return resolve(reader.result as string);
      };

      reader.onabort = () => {
        return reject();
      }

      reader.onerror = () => {
        return reject();
      }
    });
  }


}

